//* ImporTing from hooks from react
import { useEffect, useState } from "react";

//* Importing from react-bootstrap
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Container,
} from "react-bootstrap";

//! Importing from Jodit
import JoditEditor from "jodit-react";


/**
 * Represents a local sub-question within a questionnaire.
 * 
 * @typedef {Object} LocalSubQuestion
 * @property {string} localId - The unique identifier for the sub-question.
 * @property {"objective" | "dissertative"} type - The type of the sub-question, either "objective" or "dissertative".
 * @property {string} statement - The statement or question text.
 * @property {string} [answerJustification] - Optional justification for the answer.
 * @property {Array<{localId: string, text: string, isCorrect: boolean}>} [objectives] - Optional array of objective answers, each with a unique identifier, text, and a boolean indicating if it is correct.
 */
type LocalSubQuestion = {
  localId: string;
  type: "objective" | "dissertative";
  statement: string;
  answerJustification?: string;
  objectives?: Array<{
    localId: string;
    text: string;
    isCorrect: boolean;
  }>;
};

/**
 * Props for the SubQuestionModal component.
 * 
 * @property {boolean} show - Determines whether the modal is visible.
 * @property {() => void} onHide - Callback function to hide the modal.
 * @property {(data: LocalSubQuestion) => void} onSave - Callback function to save the sub-question data.
 * @property {LocalSubQuestion | null} editingData - The sub-question data being edited, or null if creating a new sub-question.
 */
type SetSubQuestionModalProps = {
  show: boolean;
  onHide: () => void;
  onSave: (data: LocalSubQuestion) => void;
  editingData: LocalSubQuestion | null;
};

/**
 * Component for displaying and editing a sub-question modal.
 *
 * @param {Object} props - The component props.
 * @param {boolean} props.show - Determines if the modal is visible.
 * @param {Function} props.onHide - Function to call when the modal is hidden.
 * @param {Function} props.onSave - Function to call when the sub-question is saved.
 * @param {Object} [props.editingData] - Data for the sub-question being edited.
 * @param {string} props.editingData.localId - The local ID of the sub-question.
 * @param {"objective" | "dissertative"} props.editingData.type - The type of the sub-question.
 * @param {string} [props.editingData.statement] - The statement of the sub-question.
 * @param {string} [props.editingData.answerJustification] - The justification for the answer.
 * @param {Array<{ localId: string; text: string; isCorrect: boolean }>} [props.editingData.objectives] - The objectives of the sub-question.
 *
 * @returns {JSX.Element} The rendered component.
 */
export function SetSubQuestionModal({
  show,
  onHide,
  onSave,
  editingData,
}: SetSubQuestionModalProps) {
  const [localId, setLocalId] = useState("");
  const [type, setType] = useState<"objective" | "dissertative">("objective");
  const [statement, setStatement] = useState("");
  const [answerJustification, setAnswerJustification] = useState("");
  const [objectives, setObjectives] = useState<
    Array<{ localId: string; text: string; isCorrect: boolean }>
  >([]);
  const [numNewObjectives, setNumNewObjectives] = useState(1); 

  // Função para converter índice em letra (a), b), c), etc.
  const indexToLetter = (index: number): string => {
    return String.fromCharCode(97 + index) + ")";
  };

  useEffect(() => {
    if (editingData) {
      setLocalId(editingData.localId);
      setType(editingData.type);
      setStatement(editingData.statement || "");
      setAnswerJustification(editingData.answerJustification || "");
      setObjectives(editingData.objectives || []);
    } else {
      setLocalId(crypto.randomUUID());
      setType("objective");
      setStatement("");
      setAnswerJustification("");
      setObjectives([]);
    }
  }, [editingData]);

  // Adicionar alternativas
  function handleAddObjectives() {
    const newObjectives = Array.from({ length: numNewObjectives }, () => ({
      localId: crypto.randomUUID(),
      text: "",
      isCorrect: false,
    }));
    setObjectives((prev) => [...prev, ...newObjectives]);
  }

  // Remover alternativa
  function handleRemoveObjective(objId: string) {
    setObjectives((prev) => prev.filter((o) => o.localId !== objId));
  }

  function handleSave() {
    if (!statement.trim()) {
      alert("O enunciado da subquestão é obrigatório.");
      return;
    }

    // Validação para modo objetivo: deve haver exatamente uma alternativa correta
    if (type === "objective") {
      const correctCount = objectives.filter((obj) => obj.isCorrect).length;
      if (correctCount !== 1) {
        alert(
          "Em modo objetivo, é necessário que haja exatamente uma alternativa correta."
        );
        return;
      }
    }

    const newData: LocalSubQuestion = {
      localId,
      type,
      statement,
      answerJustification,
      objectives: type === "objective" ? objectives : [],
    };

    onSave(newData);
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>SubQuestão</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Tipo da SubQuestão */}
        <Form.Group className="mb-3">
          <Form.Label>
            <strong>Tipo da SubQuestão</strong>
          </Form.Label>
          <Form.Select
            value={type}
            onChange={(e) => {
              const newType = e.target.value as "objective" | "dissertative";
              setType(newType);
              if (newType === "dissertative") setObjectives([]);
            }}
          >
            <option value="objective">Objetiva</option>
          </Form.Select>
        </Form.Group>

        {/* Enunciado */}
        <Form.Group className="mb-3">
          <Form.Label>
            <strong>Enunciado</strong>
          </Form.Label>
          <JoditEditor
            value={statement}
            config={{ readonly: false, toolbar: true }}
            onBlur={(content) => setStatement((content))}
          />
        </Form.Group>

        {/* Justificativa */}
        <Form.Group className="mb-3">
          <Form.Label>
            <strong>Justificativa</strong>
          </Form.Label>
          <JoditEditor
            value={answerJustification}
            config={{ readonly: false, toolbar: true }}
            onBlur={(content) => setAnswerJustification((content))}
          />
        </Form.Group>

        {/* Alternativas se for "objective" */}
        {type === "objective" && (
          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">Alternativas</h6>
            </Card.Header>
            <Card.Body>
              <Container>
                <Row className="align-items-center mb-3">
                  <Col xs={6}>
                    <Form.Select
                      value={numNewObjectives}
                      onChange={(e) =>
                        setNumNewObjectives(Number(e.target.value))
                      }
                    >
                        {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                        <option key={num} value={num}>
                          {num} Alternativa{num > 1 ? "s" : ""}
                        </option>
                        ))}
                    </Form.Select>
                  </Col>
                  <Col xs="auto" className="d-flex justify-content-center">
                    <Button variant="success" onClick={handleAddObjectives}>
                      + Adicionar
                    </Button>
                  </Col>
                </Row>
                {objectives.map((obj, index) => (
                  <Card key={obj.localId} className="mb-2">
                    <Card.Body>
                      <Row>
                        <Col xs={12} className="mb-2">
                          <strong>{indexToLetter(index)}</strong>
                        </Col>
                        <Col xs={12}>
                          <JoditEditor
                            value={obj.text}
                            config={{ readonly: false, toolbar: true }}
                            onBlur={(content) => {
                              setObjectives((prev) =>
                                prev.map((o) =>
                                  o.localId === obj.localId
                                    ? { ...o, text: (content) }
                                    : o
                                )
                              );
                            }}
                          />
                        </Col>
                        <Col
                          xs={12}
                          className="d-flex justify-content-between align-items-center mt-2"
                        >
                          <Form.Check
                            type="checkbox"
                            label="Correta?"
                            checked={obj.isCorrect}
                            onChange={(e) => {
                              // Se o usuário marcar uma alternativa, desmarca as demais
                              setObjectives((prev) =>
                                prev.map((o) =>
                                  o.localId === obj.localId
                                    ? { ...o, isCorrect: e.target.checked }
                                    : { ...o, isCorrect: false }
                                )
                              );
                            }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemoveObjective(obj.localId)}
                          >
                            Remover
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </Container>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-content-center">
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleSave}>
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
